import React, { useEffect, useState } from 'react'
import { Alert, Button, Icon, Input, Modal, Upload } from 'antd'

import { starterPrompts } from '../../constants'
import {
  formatFileSize,
  getAcceptedAttribute,
  getReferenceKind,
  isStoppableBusyAction
} from '../../utils'
import './ConversationCard.scss'

const { TextArea } = Input

const ConversationCard = ({
  sessionId,
  messages,
  timelinePanel,
  scrollRef,
  scrollAnchorRef,
  errorMessage,
  isCreatingSession,
  isStreaming,
  isSynthesizingVision,
  isUploadingReference,
  isGenerationPolling,
  isLockingVision,
  phaseBusyAction,
  isActiveWorkspaceCollapsed,
  acceptedExtensions,
  pendingReferences,
  activeWorkspace,
  draftMessage,
  isArtifactsOpen,
  onDraftMessageChange,
  onAttachReference,
  onRemovePendingReference,
  onSubmit,
  onStopAgentTurn,
  onKeyDown,
  onStarterPrompt,
  onViewArtifacts,
  activePhaseLabel = 'Creative Direction',
  activePhaseSubLabel = 'Subpage 1',
  progressPercent = 0,
  isProgressPanelCollapsed = false,
  onToggleProgressPanel = null
}) => {
  const handleStopCurrentRequest = () => {
    if (!onStopAgentTurn) return

    Modal.confirm({
      title: 'Stop current AI work?',
      content:
        'This will stop the active chat response and any visual generation polling currently running.',
      okText: 'Stop',
      cancelText: 'Keep running',
      okType: 'danger',
      centered: true,
      onOk: onStopAgentTurn
    })
  }

  const canStopCurrentRequest =
    Boolean(onStopAgentTurn) &&
    (isStreaming ||
      isUploadingReference ||
      isGenerationPolling ||
      isStoppableBusyAction(phaseBusyAction))
  const isComposerBusy =
    !sessionId ||
    isStreaming ||
    isSynthesizingVision ||
    isCreatingSession ||
    isUploadingReference ||
    isGenerationPolling ||
    isLockingVision ||
    Boolean(phaseBusyAction)
  const hasPendingReferences = Boolean(pendingReferences.length)
  const shouldShowStarterPrompts = !messages.length
  const roundedProgress = Math.max(
    0,
    Math.min(100, Math.round(progressPercent || 0))
  )
  const [showProgressToggle, setShowProgressToggle] = useState(false)

  useEffect(() => {
    const updateShowProgressToggle = () => {
      setShowProgressToggle(window.innerWidth <= 980)
    }

    updateShowProgressToggle()
    window.addEventListener('resize', updateShowProgressToggle)

    return () => {
      window.removeEventListener('resize', updateShowProgressToggle)
    }
  }, [])

  const renderStarterPrompts = () => (
    <div className="creative-direction-chat__starter-panel creative-direction-chat__starter-panel--compact">
      <div className="creative-direction-chat__starter-panel-head">
        <span>Choose a starting prompt</span>
      </div>
      <div className="creative-direction-chat__starter-grid creative-direction-chat__starter-grid--compact">
        {starterPrompts.map(item => (
          <button
            type="button"
            className={`creative-direction-chat__starter-card creative-direction-chat__starter-card--compact${item.recommended
                ? ' creative-direction-chat__starter-card--recommended'
                : ''
              }`}
            key={item.key}
            aria-label={`${item.actionLabel}: ${item.title}`}
            disabled={isComposerBusy}
            onClick={() => onStarterPrompt(item)}
          >
            {item.recommended ? (
              <em className="creative-direction-chat__starter-card-badge">
                Recommended
              </em>
            ) : null}
            <span className="creative-direction-chat__starter-card-copy">
              <strong>{item.title}</strong>
              <small>{item.description}</small>
            </span>
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <section className="creative-direction-chat__studio-card creative-direction-chat__conversation">
      <div className="creative-direction-chat__conversation-header">
        <div className="creative-direction-chat__conversation-title">
          <div className="creative-direction-chat__conversation-title-shell">
            {(showProgressToggle || isProgressPanelCollapsed) && onToggleProgressPanel ? (
              <button
                type="button"
                className="creative-direction-chat__process-open-button"
                aria-label="Open creative direction progress"
                onClick={onToggleProgressPanel}
              >
                <Icon type="menu" />
              </button>
            ) : null}
            <div className="creative-direction-chat__conversation-title-copy">
              <h2>{activePhaseLabel}</h2>
              <p>{activePhaseSubLabel}</p>
            </div>
          </div>
        </div>
        <div className="creative-direction-chat__conversation-status">
          {onViewArtifacts && !shouldShowStarterPrompts ? (
            <Button
              className="creative-direction-chat__artifacts-button"
              icon="appstore"
              aria-pressed={isArtifactsOpen ? 'true' : 'false'}
              onClick={onViewArtifacts}
            >
              {isArtifactsOpen ? 'Close Artifacts' : 'View Artifacts'}
            </Button>
          ) : null}
          <div
            className="creative-direction-chat__progress-ring"
            style={{ '--progress': String(roundedProgress) }}
            aria-label={`${roundedProgress}% complete`}
          >
            <span>{roundedProgress}%</span>
          </div>
        </div>
      </div>

      <div
        className="creative-direction-chat__conversation-scroll"
        ref={scrollRef}
      >
        <div
          className={`creative-direction-chat__messages${!messages.length ? ' creative-direction-chat__messages--empty' : ''
            }`}
        >
          {shouldShowStarterPrompts ? renderStarterPrompts() : null}

          {timelinePanel ? (
            <div className="creative-direction-chat__inline-workflow">
              {timelinePanel}
            </div>
          ) : null}
        </div>

        {errorMessage ? (
          <Alert
            className="creative-direction-chat__alert creative-direction-chat__alert--inline"
            type="error"
            showIcon
            message={errorMessage}
          />
        ) : null}
        <div
          ref={scrollAnchorRef}
          className="creative-direction-chat__scroll-anchor"
          aria-hidden="true"
        />
      </div>

      <div
        className={`creative-direction-chat__interaction-dock${activeWorkspace && !shouldShowStarterPrompts
            ? ' creative-direction-chat__interaction-dock--with-phase'
            : ''
          }${isActiveWorkspaceCollapsed
            ? ' creative-direction-chat__interaction-dock--phase-collapsed'
            : ''
          }`}
      >
        {activeWorkspace && !shouldShowStarterPrompts ? (
          <div className="creative-direction-chat__dock-phase">
            {activeWorkspace}
          </div>
        ) : null}

        <div className="creative-direction-chat__composer">
          {hasPendingReferences ? (
            <div className="creative-direction-chat__pending-files">
              {pendingReferences.map(item => (
                <div
                  className="creative-direction-chat__pending-file"
                  key={item.id}
                >
                  <span className="creative-direction-chat__pending-preview">
                    {item.previewUrl ? (
                      <img src={item.previewUrl} alt="" />
                    ) : (
                      <Icon
                        type={
                          getReferenceKind(item.file) === 'video'
                            ? 'video-camera'
                            : 'picture'
                        }
                      />
                    )}
                  </span>
                  <span className="creative-direction-chat__pending-name">
                    {item.file.name}
                    {formatFileSize(item.file.size) ? (
                      <small>{formatFileSize(item.file.size)}</small>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${item.file.name}`}
                    disabled={isComposerBusy}
                    onClick={() => onRemovePendingReference(item.id)}
                  >
                    <Icon type="close" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <div className="creative-direction-chat__composer-box">
            <div className="creative-direction-chat__upload-control">
              <Upload
                accept={getAcceptedAttribute(acceptedExtensions)}
                multiple
                showUploadList={false}
                beforeUpload={onAttachReference}
                disabled={isComposerBusy}
              >
                <button
                  type="button"
                  className="creative-direction-chat__attach-icon"
                  disabled={isComposerBusy}
                  aria-label="Attach image or video reference"
                  title="Attach image or video reference"
                >
                  <Icon type="paper-clip" />
                </button>
              </Upload>
            </div>
            <TextArea
              className="creative-direction-chat__composer-input"
              value={draftMessage}
              rows={1}
              disabled={isComposerBusy}
              aria-label="Message"
              placeholder="Type your answer or ask for a refinement..."
              onChange={event => onDraftMessageChange(event.target.value)}
              onKeyDown={onKeyDown}
            />
            {canStopCurrentRequest ? (
              <Button
                className="creative-direction-chat__stop-button"
                icon="close-circle"
                aria-label="Stop current AI work"
                title="Stop current AI work"
                onClick={handleStopCurrentRequest}
              />
            ) : (
              <Button
                type="primary"
                className="creative-direction-chat__send-button"
                loading={isUploadingReference}
                disabled={
                  (!draftMessage.trim() && !hasPendingReferences) ||
                  isComposerBusy
                }
                onClick={onSubmit}
              >
                Send
                <Icon type="arrow-right" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ConversationCard
